import i18n from "@client-shared/lib/i18n";
import { REGIONS_TO_COUNTRY_DEFAULT_LOCALE } from "@shared/lib/constants";
import { Locale } from "@shared/types";

// Helpers to get fixed translators per-locale
type FixedT = (k: string, o?: Record<string, unknown>) => string;
type I18nLike = {
  getFixedT?: (lng: string) => FixedT;
  t: FixedT;
  language: string;
  changeLanguage: (lng: string) => void;
};

export const getFixedT = (locale: Locale): FixedT => {
  const i18nTyped = i18n as unknown as I18nLike;
  const fixed = i18nTyped.getFixedT ? i18nTyped.getFixedT(locale) : undefined;
  return fixed ? (k: string, o?: Record<string, unknown>) => fixed(k, o) : (k: string, o?: Record<string, unknown>) => i18nTyped.t(k, o);
};

// Helper function to determine locale from country code
export const getLocaleFromCountryCode = (countryCode: string | undefined): Locale => {
  if (!countryCode) {
    return "en_US";
  }
  const country = countryCode.toUpperCase();
  switch (country) {
    case "US":
      return "en_US";
    case "GB":
      return "en_GB";
    case "AU":
      return "en_AU";
    case "DE":
      return "de_DE";
    case "NL":
      return "nl_NL";
    default:
      return "en_US"; // Default fallback
  }
};

// Helper function to get vendor locale from vendor region
export const getVendorLocaleFromRegion = (vendorRegion: string | undefined): Locale => {
  if (!vendorRegion) {
    return "en_US";
  }
  const key = vendorRegion as keyof typeof REGIONS_TO_COUNTRY_DEFAULT_LOCALE;
  const found = REGIONS_TO_COUNTRY_DEFAULT_LOCALE[key];
  return (found as Locale) || "en_US";
};

// Helper function to format weight with proper locale using existing i18n functions
export const formatWeightWithLocale = (weightInGrams: number | undefined, locale: Locale): string => {
  if (!weightInGrams) {
    return "-";
  }
  try {
    // Use the getFixedT helper to get a translation function for the specific locale
    // without changing the global language
    const t = getFixedT(locale);
    const formattedWeight = t("weight.weightWithSuffix", { grams: weightInGrams });
    return formattedWeight;
  } catch (error) {
    console.warn("Error formatting weight:", error);
    return `${Math.round(weightInGrams)}g`;
  }
};
